import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSupportConversation, listSupportConversations, type SupportConversationPage } from '../api/support';
import SupportNotificationSettings from './SupportNotificationSettings';
import { getAuthSession } from '../api/client';
import { getChat } from '../api/chats';
import type { ChatDto, UserBriefDto } from '../api/types';

export default function SupportConversations({ operator, onOpen, onClose }: {
  operator: boolean;
  onOpen: (chat: ChatDto, applicant?: UserBriefDto) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation('chats');
  const [items, setItems] = useState<SupportConversationPage['items']>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(false);
  const session = useRef(getAuthSession());
  const valid = () => alive.current && session.current === getAuthSession();

  async function load(before?: string | null) {
    setBusy(true);
    setError('');
    try {
      if (operator) {
        const page = await listSupportConversations(before);
        if (!valid()) return;
        setItems((previous) => before ? [...previous, ...page.items.filter((item) => !previous.some((old) => old.chatId === item.chatId))] : page.items);
        setNextBefore(page.nextBefore ?? null);
      } else {
        const chat = await getSupportConversation();
        if (valid()) await onOpen(chat);
      }
    } catch {
      if (valid()) setError(t('supportLoadFailed'));
    } finally {
      if (valid()) setBusy(false);
    }
  }

  useEffect(() => {
    alive.current = true;
    void load();

    return () => { alive.current = false; };
    // Компонент пересоздаётся при смене пользователя/роли; запрос выполняется при открытии.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function open(chatId: string, applicant: UserBriefDto) {
    setBusy(true);
    setError('');
    try {
      const chat = await getChat(chatId);
      if (valid()) await onOpen(chat, applicant);
    }
    catch { if (valid()) setError(t('supportLoadFailed')); }
    finally { if (valid()) setBusy(false); }
  }

  return <div className="modal-backdrop" onClick={onClose}>
    <div className="modal" role="dialog" aria-modal="true" aria-label={t(operator ? 'supportConversations' : 'supportChat')} onClick={(event) => event.stopPropagation()}>
      <div className="modal-header">
        <h2>{t(operator ? 'supportConversations' : 'supportChat')}</h2>
        <button className="icon-btn" onClick={onClose} aria-label={t('supportClose')}>×</button>
      </div>
      <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        {operator && <SupportNotificationSettings />}
        {operator && items.map((item) => <button key={item.chatId} className="menu-item" disabled={busy} onClick={() => void open(item.chatId, item.applicant)}>
          <span><strong>{item.applicant.displayName || item.applicant.username}</strong> (@{item.applicant.username})
            <span style={{ display: 'block', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{typeof item.lastMessage?.payload.data?.text === 'string' ? item.lastMessage.payload.data.text.slice(0, 160) : t('supportNoMessages')}</span>
          </span>
          {item.unread > 0 && <span className="badge">{item.unread}</span>}
        </button>)}
        {operator && !busy && !error && items.length === 0 && <p>{t('supportEmpty')}</p>}
        {busy && <p role="status">{t('supportLoading')}</p>}
        {error && <p role="alert" className="error">{error}</p>}
        {error && <button className="btn" disabled={busy} onClick={() => void load(items.length ? nextBefore : null)}>{t('supportRetry')}</button>}
        {operator && nextBefore && !error && <button className="btn" disabled={busy} onClick={() => void load(nextBefore)}>{t('supportMore')}</button>}
        {operator && !busy && <button className="btn" onClick={() => void load()}>{t('supportRefresh')}</button>}
      </div>
    </div>
  </div>;
}
